import {
  MultiFactorInfo,
  PhoneMultiFactorInfo,
  TotpMultiFactorInfo,
} from 'firebase/auth';
import * as React from 'react';
import styled from 'styled-components';
import { ErrorText } from '../../../../shared/form/ErrorMessage';
import { PhoneHint } from './PhoneHint';
import { TotpHint } from './TotpHint';

export interface ISecondFactorProps {
  hints: MultiFactorInfo[];
  onSelectFactor: (value: MultiFactorInfo) => void;
  selectedFactor?: MultiFactorInfo;
  error?: string;
}

export const SecondFactor = React.memo(
  (props: ISecondFactorProps): React.ReactElement => {
    const handleFactorClick = React.useCallback(
      (hint: MultiFactorInfo): void => {
        if (props.selectedFactor?.uid === hint.uid) {
          // Don't deselect - user must choose a factor
          return;
        }
        props.onSelectFactor(hint);
      },
      [props.selectedFactor, props.onSelectFactor]
    );

    const errorId = 'verification-method-error';

    return (
      <React.Fragment>
        <h1 id='verification-method-heading' style={{ margin: '0.25rem 1rem 0.5rem' }}>
          Select Verification Method
        </h1>
        <InputContainer role='form' aria-labelledby='verification-method-heading'>
          <FactorList role='radiogroup'>
            {props.hints.map(
              (hint: MultiFactorInfo, index: number): React.ReactElement => {
                if (hint.factorId === 'phone') {
                  return (
                    <PhoneHint
                      selected={props.selectedFactor?.uid === hint.uid}
                      hint={hint as PhoneMultiFactorInfo}
                      onClick={handleFactorClick}
                      animationDelay={index}
                      key={hint.uid}
                    />
                  );
                } else if (hint.factorId === 'totp') {
                  return (
                    <TotpHint
                      selected={props.selectedFactor?.uid === hint.uid}
                      hint={hint as TotpMultiFactorInfo}
                      onClick={handleFactorClick}
                      animationDelay={index}
                      key={hint.uid}
                    />
                  );
                } else {
                  return null;
                }
              }
            )}
          </FactorList>
          {props.error && (
            <ErrorText id={errorId} role='alert' aria-live='polite'>
              {props.error}
            </ErrorText>
          )}
        </InputContainer>
      </React.Fragment>
    );
  },
  (
    prevProps: Readonly<ISecondFactorProps>,
    nextProps: Readonly<ISecondFactorProps>
  ): boolean => {
    return (
      prevProps.hints === nextProps.hints &&
      prevProps.selectedFactor?.uid === nextProps.selectedFactor?.uid &&
      prevProps.error === nextProps.error &&
      prevProps.onSelectFactor === nextProps.onSelectFactor
    );
  }
);

SecondFactor.displayName = 'SecondFactor';

const InputContainer = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: flex-start;
  padding: 0px 16px 8px;
  width: 100%;
  height: 100%;
  overflow-y: auto;
`;

const FactorList = styled.div`
  display: flex;
  flex-direction: column;
  width: 100%;
`;
